// src/hooks/useActivityLogger.js
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

/* ==== Configurable Constants ==== */
const DB_NAME = "UserActivityDB";
const DB_VERSION = 1;
const STORE_NAME = "userActivityLogs";
const USER_DETAILS_KEY = "userDetails";
const TIME_SPENT_THRESHOLD_MS = 500;

/* ==== Internal State ==== */
let lastVisitedAt = Date.now();
let lastPath = window.location.pathname;

/* ==== IndexedDB Open ==== */
function openDatabase() {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { keyPath: "id", autoIncrement: true });
            }
        };

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

/* ==== Utility Functions ==== */
function sanitizeForStorage(obj) {
    const clone = {};
    Object.entries(obj).forEach(([key, value]) => {
        if (value === null || ["string", "number", "boolean"].includes(typeof value)) {
            clone[key] = value;
        }
    });
    return clone;
}

function getUserId() {
    try {
        const raw = localStorage.getItem(USER_DETAILS_KEY);
        if (raw) {
            const userDetails = JSON.parse(raw);
            return userDetails?._id || null;
        }
    } catch (err) {
        console.error("Failed to parse userDetails from localStorage:", err);
    }
    return null;
}

function storeActivity(activity) {
    const uid = activity.userId;
    const sanitizedActivity = sanitizeForStorage(activity);

    openDatabase().then((db) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        const store = tx.objectStore(STORE_NAME);
        store.add({
            ...sanitizedActivity,
            uid,
            timestamp: new Date().toISOString(),
            saved: false,
        });
        tx.oncomplete = () => db.close();
    });
}

function logTimeSpent(userId) {
    const now = Date.now();
    const timeSpentMs = now - lastVisitedAt;

    if (lastPath && timeSpentMs > TIME_SPENT_THRESHOLD_MS) {
        storeActivity({
            type: "time-spent",
            path: lastPath,
            durationMs: timeSpentMs,
            durationSec: Math.round(timeSpentMs / 1000),
            userId
        });
    }

    lastVisitedAt = now;
    lastPath = window.location.pathname;
}

/* ==== Main Hook ==== */
export function useActivityLogger() {
    const initialized = useRef(false);
    const userDetailReducer = useSelector(
        (state) => state.basicConfigReducer?.userdetails
    );
    const userId = userDetailReducer?._id || getUserId() || "NA";

    useEffect(() => {
        if (initialized.current) return;
        initialized.current = true;

        // 1. Page load
        storeActivity({ type: "page-load", url: window.location.href, userId });

        // 2. Navigation (SPA)
        const handlePopState = () => {
            storeActivity({ type: "navigation", url: window.location.href, userId });
        };
        window.addEventListener("popstate", handlePopState);

        // 3. Clicks
        const handleClick = (e) => {
            const target = e.target.closest("[data-track], button, a, input, textarea, select, label");
            if (target) {
                storeActivity({
                    type: "click",
                    element: target.tagName,
                    class: target.className,
                    text: target.innerText?.slice(0, 100),
                    userId
                });
            }
        };
        document.addEventListener("click", handleClick);

        // 4. Time spent before unload
        const handleBeforeUnload = () => {
            logTimeSpent(userId);
        };
        window.addEventListener("beforeunload", handleBeforeUnload);

        /* === Optional: Enable as needed ===

        // 5. Input tracking
        const handleInput = (e) => {
            const target = e.target;
            if (["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) {
                storeActivity({
                    type: "input",
                    element: target.tagName,
                    id: target.id,
                    name: target.name,
                    value: target.value.slice(0, 100),
                });
            }
        };
        document.addEventListener("change", handleInput);

        // 6. Scroll tracking
        const handleScroll = () => {
            storeActivity({
                type: "scroll",
                scrollY: window.scrollY,
            });
        };
        window.addEventListener("scroll", handleScroll);

        // 7. Key press tracking
        const handleKeydown = (e) => {
            storeActivity({
                type: "keydown",
                key: e.key,
                target: e.target.tagName,
            });
        };
        document.addEventListener("keydown", handleKeydown);

        ===================================== */

        return () => {
            window.removeEventListener("popstate", handlePopState);
            document.removeEventListener("click", handleClick);
            window.removeEventListener("beforeunload", handleBeforeUnload);

            // Optional cleanup
            // document.removeEventListener("change", handleInput);
            // window.removeEventListener("scroll", handleScroll);
            // document.removeEventListener("keydown", handleKeydown);
        };
    }, [userId]);

    // For logging from custom events
    const logCustomActivity = (data) => {
        storeActivity({ type: "custom", ...data, userId });
    };

    return { logCustomActivity };
}
